import type { AgentDefinition, AgentOptions, ModelSelection } from "../../../types/agent.js";
import type {
  Run,
  RunOperation,
  RunStatus,
  SDKUserMessage,
  SendOptions,
} from "../../../types/run.js";
import { DEFAULT_AGENTIC_MODEL_ID } from "../config/default-model.js";
import { buildFixtureScript } from "../fixtures/fixture-responder.js";
import { FixtureRunBase, prepareRunContext } from "../fixtures/fixture-run-base.js";
import type { MemoryFact } from "../memory/memory-store.js";
import { registerRun } from "../registry/run-registry.js";
import type { SessionMessage } from "../session/agent-session.js";

/**
 * Options accepted by `createLocalRun()`.
 *
 * @internal
 */
export interface CreateLocalRunOptions {
  agentId: string;
  model: ModelSelection | undefined;
  message: string | SDKUserMessage;
  agentOptions: AgentOptions;
  sendOptions: SendOptions;
  workspaceCwd: string;
  subagents: Record<string, AgentDefinition>;
  settingSourcesIncludeProject: boolean;
  memoryFacts: MemoryFact[];
  sessionMessages: SessionMessage[];
  projectMcpServers: Record<string, unknown>;
  persistMemoryFact?: (fact: MemoryFact) => Promise<void>;
  /**
   * Accepted for API parity with the real runtime; the fixture responder is
   * deterministic and does not feed the prompt into its scripts.
   */
  systemPrompt?: string;
}

/**
 * Build a local Run handle backed by the in-process fixture responder.
 *
 * @internal
 */
export function createLocalRun(options: CreateLocalRunOptions): Run {
  const { userText, id, startTime } = prepareRunContext(options.message);
  const supported = new Set<RunOperation>(["stream", "wait", "cancel", "conversation"]);

  const script = buildFixtureScript({
    agentId: options.agentId,
    runId: id,
    model: options.model ?? { id: DEFAULT_AGENTIC_MODEL_ID },
    userMessage: userText,
    runtime: "local",
    agentOptions: options.agentOptions,
    sendOptions: options.sendOptions,
    workspaceCwd: options.workspaceCwd,
    subagents: options.subagents,
    settingSourcesIncludeProject: options.settingSourcesIncludeProject,
    memoryFacts: options.memoryFacts,
    sessionMessages: options.sessionMessages,
    projectMcpServers: options.projectMcpServers,
    ...(options.persistMemoryFact !== undefined
      ? { persistMemoryFact: options.persistMemoryFact }
      : {}),
  });

  // SE3 — capture the turn's provenance so `buildResult` forwards it onto RunResult.origin.
  if (options.sendOptions.origin !== undefined) script.origin = options.sendOptions.origin;

  const handle = new LocalRun({
    id,
    agentId: options.agentId,
    model: options.model,
    script,
    supportedOps: supported,
    startTime,
  });
  handle.bootstrap();
  registerRun(handle);
  return handle;
}

class LocalRun extends FixtureRunBase {
  bootstrap(): void {
    if (this.script.cancellable) return;
    setTimeout(() => {
      void this.completeNaturally();
    }, 0);
  }

  protected override notifyImmediately(): boolean {
    // Local agents emit the "running" state synchronously to subscribers;
    // tests rely on observing the transition even when added pre-completion.
    return true;
  }

  private async completeNaturally(): Promise<void> {
    if (this.terminated) return;
    const beforeOk = await this.runBeforeComplete();
    if (!beforeOk) return;
    this.transitionTo(this.script.finalStatus);
  }

  /** Runs the optional beforeComplete hook. Returns false iff it failed and the run transitioned to error. */
  private async runBeforeComplete(): Promise<boolean> {
    if (this.script.beforeComplete === undefined) return true;
    try {
      await this.script.beforeComplete();
      return true;
    } catch (cause) {
      // Capture the beforeComplete failure on the script so the wait()
      // caller sees `result.error.message` instead of an opaque status.
      if (this.script.errorDetail === undefined) {
        const message = cause instanceof Error ? cause.message : String(cause);
        this.script.errorDetail = { message, code: "before_complete_failed", cause };
      }
      this.transitionTo("error" satisfies RunStatus);
      return false;
    }
  }
}
